import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Button, IconButton, InputAdornment, Paper, TextField } from '@mui/material';
import { useConfig, getNestedString } from '@/utils/useConfig';
import useParams from '@fuse/hooks/useParams';
import useNavigate from '@fuse/hooks/useNavigate';
import apiFetch from '@/utils/apiFetch';
import Landing from '@/components/Landing';
import Link from '@fuse/core/Link';
import { handleApiError } from '@/utils/errorHandler';

import { useEffect, useState } from 'react';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import { Helmet } from 'react-helmet';

const schema = z.object({
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*+])[A-Za-z\d!@#$%^&*+]{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  passwordConfirm: z
    .string()
    .min(1, 'Password confirmation is required')
}).refine((data) => data.password === data.passwordConfirm, {
  message: 'Passwords must match',
  path: ['passwordConfirm'],
});

type FormValues = z.infer<typeof schema>;

export default function ConfirmAddUser() {
  const { config } = useConfig('auth_pages_config');
  const { key } = useParams();
  const navigate = useNavigate();

  useEffect(() => {

    // Remove JWT token from localStorage if present
    const token = localStorage.getItem('jwt_access_token');

    if (token) {
      setIsLoggingOut(true);
      localStorage.removeItem('jwt_access_token');
      // Also remove refresh token if it exists
      localStorage.removeItem('jwt_refresh_token');
      globalThis.location.reload();
    }
  }, []);

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      passwordConfirm: ''
    }
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await apiFetch('/portal/account/users/add-verify/', {
        method: 'POST',
        body: JSON.stringify({ key, password: data.password })
      });

      toast.success('Email confirmed. Please sign in');
      navigate('/sign-in');
    } catch (error_) {
      handleApiError(error_, { 'messagePrefix': 'Error creating your account.' });
      navigate('/sign-in');
    }
  };

  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const [confirmPasswordVisibility, setConfirmPasswordVisibility] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  // Don't show the page until JWT removal and reload is complete
  if (isLoggingOut) {
    return (
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center">
        <div>Clearing session...</div>
      </div>
    );
  }

  return (
    <>
        <Helmet>
            <meta charSet="utf-8" />
            <title>{getNestedString(config, 'confirmAddUser.metadata.title')}</title>
            <meta
                name="description"
                content={getNestedString(config, 'confirmAddUser.metadata.description')}
            />
        </Helmet>

    <div className="flex min-w-0 flex-1 flex-col items-center sm:flex-row sm:justify-center md:items-start md:justify-start">
      <Paper className="h-full w-full px-16 py-8 ltr:border-r-1 rtl:border-l-1 sm:h-auto sm:w-auto sm:rounded-xl sm:p-48 sm:shadow md:flex md:h-full md:w-1/2 md:items-center md:justify-end md:rounded-none md:p-64 md:shadow-none">
        <div className="mx-auto w-full max-w-320 sm:mx-0 sm:w-320">
          <img
                className="mx-auto"
                src={getNestedString(config, 'image')}
                alt="logo"
            />
          <h1 className="mt-32 text-4xl font-extrabold leading-tight tracking-tight">
            Create Your Account
          </h1>

          <div className="mt-2 flex items-baseline font-medium">
            <p>Already have an account?</p>
            <Link className="ml-4" to="/sign-in">
              Sign in
            </Link>
          </div>

          <form
            className="mt-24 flex flex-col space-y-20"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Enter password"
                  type={passwordVisibility ? 'text' : 'password'}
                  autoComplete='new-password'
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  variant="outlined"
                  required
                  onPaste={() => {
                    // Retrigger validation after paste
                    setTimeout(() => trigger('password'), 0);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              passwordVisibility
                                ? 'hide the password'
                                : 'display the password'
                            }
                            onClick={() => setPasswordVisibility(!passwordVisibility)}
                            onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                              event.preventDefault()
                            }
                            onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                              event.preventDefault()
                            }
                            edge="end"
                          >
                            {passwordVisibility ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="passwordConfirm"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirm password"
                  autoComplete='new-password'
                  type={confirmPasswordVisibility ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.passwordConfirm}
                  helperText={errors.passwordConfirm?.message}
                  variant="outlined"
                  required
                  onPaste={() => {
                    // Retrigger validation after paste
                    setTimeout(() => trigger(['password', 'passwordConfirm']), 0);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              confirmPasswordVisibility
                                ? 'hide the password'
                                : 'display the password'
                            }
                            onClick={() => setConfirmPasswordVisibility(!confirmPasswordVisibility)}
                            onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                              event.preventDefault()
                            }
                            onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                              event.preventDefault()
                            }
                            edge="end"
                          >
                            {confirmPasswordVisibility ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={isSubmitting}
              fullWidth
            >
              Confirm
            </Button>
          </form>
        </div>
      </Paper>

      <Landing
        title={getNestedString(config, 'landingTitle')}
        description={getNestedString(config, 'confirmAddUser.landingDescription')}
      />
    </div>
    </>
  );
}
